import { defineStore } from 'pinia';
import type {
  SitemapItem,
  StoryBlokAnyContentPage,
  StoryBlokApiResponseSiteSettings,
  StoryBlokConfig,
  StoryBlokGlobalFooter,
  StoryBlokHeaderData,
  StoryBlokLink,
  StoryBlokNestableNav,
  StoryBlokSiteNoticeData,
  StoryBlokSiteSettingsCombined
} from '#constants/types/storyblok';

import type { DomainMapper } from '~/util/domain-mapper';
import { findDomainByHostAndPath, findMarketByCountryCode, getCountryNativeName, getMarketsByEnvironment } from '~/util/domain-mapper';
import useLinkReplacer from '~/composables/useLinkReplacer';
import type { CrossDockingPrices, FilterConfiguration } from '#constants/types/algolia';
import useApiFetch from '~/composables/useApiFetch';
import useMenuHelper from '~/composables/useMenuHelper';

import { useUserStore } from '~/store/user';
import { useCartStore } from '~/store/cart';
import useStoryblokAdmin from '~/composables/useStoryblokAdmin';

import { useCustomerTypeCookie, useB2bB2cUserSelectedCookie } from '~/composables/useGlobalCookies';

const { replaceLinksInStoryBlokResponse } = useLinkReplacer();

type Footer = null | StoryBlokGlobalFooter;
type Header = null | StoryBlokHeaderData;
type Config = null | StoryBlokConfig;
type SiteNotice = null | StoryBlokSiteNoticeData;
type DomainMap = null | DomainMapper;

type CustomerType = 'b2b' | 'b2c' | null;

// Pure helper functions that accept customerType as a parameter.
// This avoids re-entrant store access and ensures proper reactivity
// when used in Pinia getters (getters pass state.customerType directly).
const isB2b = (ct: CustomerType) => ct === 'b2b';
const isB2c = (ct: CustomerType) => ct === 'b2c';
const isCustomerTypeSet = (ct: CustomerType) => ct !== null && ct !== undefined;

const isB2bEnabled = (domainMap: DomainMap) => domainMap?.b2b?.enabled ?? false;
const isB2cEnabled = (domainMap: DomainMap) => (domainMap?.b2c?.enabled ?? false);

const getConsumerTypeSettingsPathFromDomain = (domainMap: DomainMap, ct: CustomerType): string => {
  const basePath = domainMap?.storyblokBasePath ?? '';
  const b2bPath = domainMap?.b2b?.storyblokBasePath ?? '';
  const b2cPath = domainMap?.b2c?.storyblokBasePath ?? '';

  if (domainMap?.b2b?.enabled && isB2b(ct) && b2bPath.length) {
    return `${basePath}/${b2bPath}/`;
  }

  if (domainMap?.b2c?.enabled && isB2c(ct) && b2cPath.length) {
    return `${basePath}/${b2cPath}/`;
  }

  return basePath.length ? `${basePath}/` : '';
};

export type StoriesEntryLimited = {
  id: number;
  name: string;
  parent_id: number;
  uuid: string;
  real_path: string;
}
export type BrandItem = {
  brandCode: string,
  brandName: string,
  brandFlag: string;
  brandLink: string;
  brandCategory: string;
  brandLogo: string | null;
  showInAutomaticList: boolean;
}

// markets that get the new B2C design (PDP, PLP and find retailer), AT keeps the old one
const NEW_DESIGN_COUNTRIES = ['se', 'no', 'dk', 'gb', 'fi', 'fr'];

export const useGlobalContentStore = defineStore('globalContentStore', {
  state: () => ({
    articleKeywords: [] as string[],
    config: null as Config,
    customerType: null as 'b2b' | 'b2c' | null,
    countryDomains: [] as { flagUrl: string, countryCode: string, countryUrl: string }[],
    currentStory: null as null | StoryBlokAnyContentPage,
    detectedCountryCode: '' as string,
    domainMap: null as DomainMap,
    footer: null as Footer,
    header: null as Header,
    hostname: '' as string,
    pageId: -1, // storyblok
    pageUuid: '', // storyblok page uuid
    siteNotice: null as SiteNotice,
    stories: [] as StoriesEntryLimited[],
    translations: {} as { [key: string]: string; },
    filterSettings: {
      filterValues: [],
      filters: [],
      sortIndices: [],
      translations: {},
      flagSuffix: '',
      maximumDiscountForOrderProposals: 100,
    } as FilterConfiguration,
    filterSettingsLoaded: false,
    sitemap: [] as SitemapItem[],
    sitemapLoaded: false,
    brandList: [] as BrandItem[],
    brandListLoaded: false,
    brandListLoading: false,
    pointCollectingThreshold: 1000,
    cv: null as number | null,
    crossDockingPrices: null as null | CrossDockingPrices,
    consumerFeePartNo: null as null|string,
    consumerFeePrice: null as null|number|undefined,
    enableOrderProposalRequest: null as boolean | null,
    restrictOrderProposalToCsOnly: null as boolean | null,
    orderProposalRequestAreaThreshold: 500,// null as null|number,
    palletDiscountPartNo: null as null|string,
    outsideModDeliveryFeePartNo: null as null|string,
    outsideModDeliveryFeePrice: null as null|number|undefined,
    tailLiftPartNo: null as null|string,
    tailLiftPrice: null as null|number|undefined,
    timedUnloadingPartNo: null as null|string,
    timedUnloadingPrice: null as null|number|undefined,
    maxDiscount: 100 as number,
    maxDiscountSalesrep: 100 as number,
    orderBeforeDays: null as null|number,
    orderBeforeHour: null as null|number,
    orderBeforeMinute: null as null|number,
    surchargePricePerUnit: null as null|number,
    norceCheckoutEnabled: false as boolean,
    showSurchargeNoticeForFlaggedCompanies: false as boolean,
    showSurchargeNoticeForAllCompanies: false as boolean,
  }),
  actions: {
    async loadBrands() {
      // Kährs dont need this
      return false;
    },
    async loadSitemap() {
      if (this.sitemapLoaded) {
        return true;
      }
      if (this.domainMap?.features?.nonEcomSite) {
        return true;
      }
      const { apiGet, handleApiError } = useApiFetch();
      const res = await apiGet<SitemapItem[]>(
        `/applications/breadcrumbs?type=${useRuntimeConfig().public.storyblokStoriesVersion}`
      );
      if (!res) {
        handleApiError();
      } else {
        this.sitemap = res;
        this.sitemapLoaded = true;
      }
      return true;
    },
    async loadSiteSettings() {
      if (this.filterSettingsLoaded) {
        return true;
      }
      const { apiGet, handleApiError } = useApiFetch();
      const locale = this.domainMap?.locale || '';
      const environment = this.domainMap?.environment || 'local';
      const isNonEcom = this.domainMap?.features?.nonEcomSite ?? false;
      const res = await apiGet<FilterConfiguration>(`/applications/configuration?lang=${locale}`);

      if (!res) {
        console.error('Couldn\'t load /applications/configuration');
        handleApiError();
        const lang = await import('~/lang/local.json');
        this.translations = lang.default;
      } else {

        if (environment === 'local') {
          const lang = await import('~/lang/local.json');
          this.translations = {
            ...lang.default,
            ...res.translations,
          };
        } else {
          this.translations = res.translations;
        }

        // Non-ecom sites only need translations — skip all filter/delivery/ecom state
        if (isNonEcom) {
          this.filterSettingsLoaded = true;
          return true;
        }

        res.translations = {};
        this.filterSettings = res;
        this.filterSettingsLoaded = true;

        if (res.crossDocking) {
          this.crossDockingPrices = res.crossDocking;
        }
        if (res.consumerFeePartNo) {
          this.consumerFeePartNo = res.consumerFeePartNo;
          this.consumerFeePrice = res.consumerFeePrice;
        }

        if (res.enableOrderProposalRequest) {
          this.enableOrderProposalRequest = res.enableOrderProposalRequest;
        }

        if (res.restrictOrderProposalToCsOnly) {
          this.restrictOrderProposalToCsOnly = res.restrictOrderProposalToCsOnly;
        }
        if (res.palletDiscountPartNo) {
          this.palletDiscountPartNo = res.palletDiscountPartNo;
        }
        if (res.outsideModDeliveryFeePartNo) {
          this.outsideModDeliveryFeePartNo = res.outsideModDeliveryFeePartNo;
          this.outsideModDeliveryFeePrice = res.outsideModDeliveryFeePrice;
        }
        if (res.tailLiftPartNo) {
          this.tailLiftPartNo = res.tailLiftPartNo;
          this.tailLiftPrice = res.tailLiftPrice;
        }
        if (res.timedUnloadingPartNo) {
          this.timedUnloadingPartNo = res.timedUnloadingPartNo;
          this.timedUnloadingPrice = res.timedUnloadingPrice;
        }
        if (res.tailLiftPartNo) {
          this.tailLiftPartNo = res.tailLiftPartNo;
          this.tailLiftPrice = res.tailLiftPrice;
        }
        if (res.orderBeforeDays) {
          this.orderBeforeDays = res.orderBeforeDays;
          this.orderBeforeHour = res.orderBeforeHour ?? null;
          this.orderBeforeMinute = res.orderBeforeMinute ?? null;
        }
        if (res.surchargePricePerUnit !== undefined) {
          this.surchargePricePerUnit = res.surchargePricePerUnit;
        }
        if (res.showSurchargeNoticeForFlaggedCompanies !== undefined) {
          this.showSurchargeNoticeForFlaggedCompanies = res.showSurchargeNoticeForFlaggedCompanies;
        }
        if (res.showSurchargeNoticeForAllCompanies !== undefined) {
          this.showSurchargeNoticeForAllCompanies = res.showSurchargeNoticeForAllCompanies;
        }

        if (res.maximumDiscountForOrderProposals) {
          this.maxDiscount = res.maximumDiscountForOrderProposals ?? 100;
        }
        if (res.maximumDiscountForOrderProposalsSalesrep) {
          this.maxDiscountSalesrep = res.maximumDiscountForOrderProposalsSalesrep ?? 100;
        }
        if (res.norceCheckoutEnabled !== undefined) {
          this.norceCheckoutEnabled = res.norceCheckoutEnabled;
        }
      }
      return true;
    },
    async refreshDeliveryExtrasPrices() {
      if (import.meta.server) {
        return;
      }

      const userStore = useUserStore();

      const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

      const waitForPrice = async(partNo: string) => {
        const maxAttempts = 30;

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const priceState = userStore.getPrice(partNo);

          if (priceState.status === 'ok' && priceState.price) {
            const raw = priceState.price.priceBeforeVat ?? priceState.price.price;
            const parsed = raw ? Number.parseFloat(raw) : NaN;
            if (!Number.isNaN(parsed)) {
              return parsed;
            }
            return null;
          }

          if (priceState.status === 'error' || priceState.status === 'notLoggedIn') {
            return null;
          }

          await delay(150);
        }

        console.warn(`Timed out loading price for part number ${partNo}`);
        return null;
      };

      const entries = [
        {
          partNo: this.outsideModDeliveryFeePartNo,
          apply: (price: number | null) => {
            if (typeof price === 'number') {
              this.outsideModDeliveryFeePrice = price;
            }
          },
        },
        {
          partNo: this.tailLiftPartNo,
          apply: (price: number | null) => {
            if (typeof price === 'number') {
              this.tailLiftPrice = price;
            }
          },
        },
        {
          partNo: this.timedUnloadingPartNo,
          apply: (price: number | null) => {
            if (typeof price === 'number') {
              this.timedUnloadingPrice = price;
            }
          },
        },
        {
          partNo: this.consumerFeePartNo,
          apply: (price: number | null) => {
            if (typeof price === 'number') {
              this.consumerFeePrice = price;
            }
          },
        },
      ];

      await Promise.all(entries.map(async({ partNo, apply }) => {
        if (!partNo) {
          return;
        }
        const price = await waitForPrice(partNo);
        apply(price);
      }));
    },
    async setCorrectLangAndMarket() {
      const hostname = useRequestHeaders(['host'])?.host ?? '';
      const detectedCountryCode = useRequestHeaders(['cf-ipcountry'])?.['cf-ipcountry']?.toLowerCase() ?? '';
      const domainMap = findDomainByHostAndPath(hostname, useRoute().path);
      this.hostname = hostname;
      this.domainMap = domainMap;
      this.detectedCountryCode = detectedCountryCode;
      this.setInitialEndConsumerType();
    },
    setInitialEndConsumerType() {
      // Seed state from cookie so isCustomerTypeSet() works before setIsB2b() is called
      const customerTypeCookie = useCustomerTypeCookie();
      this.customerType = customerTypeCookie.value ?? null;

      const storyblokAdmin = useStoryblokAdmin();
      const userSelected = useB2bB2cUserSelectedCookie();
      const domainMap = this.domainMap;
      const route = useRoute();

      const b2bEnabled = domainMap?.b2b?.enabled ?? false;
      const b2cEnabledRaw = domainMap?.b2c?.enabled ?? false;
      const b2cEnabledIncludingPreview = isB2cEnabled(domainMap);

      const hasDifferentEndConsumerTypes = b2bEnabled && b2cEnabledIncludingPreview;

      // If only one type exists
      if (!b2bEnabled || !b2cEnabledRaw) {
        this.setIsB2b(b2bEnabled);
        userSelected.value = 'true';
        return;
      }

      const forceConsumerTypeParam = route.query?.forceConsumerType;
      const forceConsumerType = Array.isArray(forceConsumerTypeParam) ? forceConsumerTypeParam[0] : forceConsumerTypeParam;

      // A direct link to the b2b/b2c startpage should decide b2b or b2c
      const normalizedPath = route.path?.replace(/\/?$/, '/');
      const isDirectB2bStartpage = hasDifferentEndConsumerTypes && normalizedPath === this.getB2bStartpagePath;
      const isDirectB2cStartpage = hasDifferentEndConsumerTypes && normalizedPath === this.getB2cStartpagePath;

      if (forceConsumerType?.length && hasDifferentEndConsumerTypes) {
        this.setIsB2b(forceConsumerType === 'b2b');
        userSelected.value = 'true';
      } else if (isDirectB2bStartpage || isDirectB2cStartpage) {
        this.setIsB2b(isDirectB2bStartpage);
        userSelected.value = 'true';
      } else if (!isCustomerTypeSet(this.customerType) || storyblokAdmin.isInStoryblokAdmin()) {
        const path = route.path;
        const b2bPath = domainMap?.b2b?.storyblokBasePath ?? '';
        if (b2bEnabled && b2bPath.length && (path.includes(`/${b2bPath}/`) || path.endsWith(`/${b2bPath}`))) {
          this.setIsB2b(true);
        } else {
          this.setIsB2b(false);
        }
      }
    },
    async loadFromCms() {
      const storyblokApi = useStoryblokApi();
      const runtimeConfig = useRuntimeConfig();
      const consumerTypeSettingsPath = getConsumerTypeSettingsPathFromDomain(this.domainMap, this.customerType);
      const res = await storyblokApi.get('cdn/stories/', {
        version: runtimeConfig.public.storyblokStoriesVersion as 'draft' | 'published',
        starts_with: `${consumerTypeSettingsPath}sitesettings/`,
        resolve_links: 'url', // Warining: if you change this to 'story' or '1' the hydration will be over 1mb (~ 400% more than current)
        filter_query: {
          component: { not_in: 'SalesToolsSettings' },
        },
        cv: Date.now(),
      }) as StoryBlokApiResponseSiteSettings;
      this.cv = res.data.cv;

      const globalData = replaceLinksInStoryBlokResponse<StoryBlokSiteSettingsCombined[]>(res.data.stories);

      const footer = globalData.find((i) => {
        return i.content.component === 'GlobalFooter';
      });

      if (footer) {
        this.footer = footer.content as StoryBlokGlobalFooter;
      } else {
        console.warn('Couldn\'t find Footer in globalContentStore');
      }

      const navigation = globalData.find((i) => {
        return i.content.component === 'HeaderData';
      });

      if (navigation) {
        this.header = navigation.content as StoryBlokHeaderData;
      } else {
        console.warn('Couldn\'t find Navigation in globalContentStore');
      }

      const config = globalData.find((i) => {
        return i.content.component === 'Config';
      });

      if (config) {
        this.config = config.content as StoryBlokConfig;
      } else {
        console.warn('Couldn\'t find Config in globalContentStore');
      }

      const siteNotice = globalData.find((i) => {
        return i.content.component === 'SiteNoticeSettings';
      });

      if (siteNotice) {
        this.siteNotice = {
          ...siteNotice.content,
          hideSiteMessage: false,
        } as StoryBlokSiteNoticeData;
      } else {
        console.warn('Couldn\'t find Site Notice in globalContentStore');
      }
    },
    async getArticleKeywords() {
      if (this.articleKeywords.length === 0) {
        const storyblokApi = useStoryblokApi();
        // @ts-ignore
        const { data } = await storyblokApi.get('cdn/datasource_entries', {
          datasource: 'article-keywords',
        }).catch(error => {
          console.log(error);
        });
        if (data.datasource_entries.length) {
          const keywordSuffix = this.domainMap?.keywordSuffix ?? '';
          this.articleKeywords = data.datasource_entries.filter((i: any) => i.value.indexOf('--') === -1 || i.value.endsWith(keywordSuffix)).map((i: any) => {
            return i.value;
          });
        }
      }
      return this.articleKeywords;
    },
    setPageUuid(payload: string) {
      this.pageUuid = payload;
    },
    setPageId(payload: number) {
      this.pageId = payload;
    },
    setCurrentStory(payload: StoryBlokAnyContentPage) {
      this.currentStory = payload;
    },
    setHideSiteMessage(payload: boolean) {
      if (this.siteNotice) {
        this.siteNotice = {
          ...this.siteNotice,
          hideSiteMessage: payload,
        };
      }
    },
    setIsB2b(isB2b = true) {
      const value: 'b2b' | 'b2c' = isB2b ? 'b2b' : 'b2c';
      this.customerType = value;
      const cookieRef = useCustomerTypeCookie();
      cookieRef.value = value;
    },
  },
  getters: {
    isSubFolder(state) {
      return state.domainMap?.subFolder ?? false;
    },
    baseHref(state) {
      return state.domainMap?.url ?? '/';
    },
    keywordSuffix(state) {
      return state.domainMap?.keywordSuffix ?? '';
    },
    getPrependPathForManualLinksWithoutLeadingSlash(state) {
      return (state.domainMap?.subFolder ?? false) ? `${state.domainMap?.storyblokBasePath}` : '';
    },
    getPrependPathForManualLinksWithTrailingSlash(state) {
      return (state.domainMap?.subFolder ?? false) ? `${state.domainMap?.storyblokBasePath}/` : '';
    },
    getPrependPathForManualLinks(state) {
      return (state.domainMap?.subFolder ?? false) ? `/${state.domainMap?.storyblokBasePath}` : '';
    },
    getSitemapItemById: (state) => (id: number) => {
      return state.sitemap.find((f) => f.id === id);
    },
    getSitemapItemBySlug: (state) => (slug: string, stripSlash = false) => {
      if (stripSlash && slug.at(-1) === '/') {
        slug = slug.slice(0, -1);
      }
      return state.sitemap.find((f) => f.full_slug === slug);
    },
    getSitemapItemByParentId: (state) => (id: number) => {
      return state.sitemap.filter((f) => f.parent_id === id);
    },
    getSitemapIndexPageFromFolder: (state) => (folderId: number) => {
      return state.sitemap.find((f) => f.parent_id === folderId && f.is_startpage);
    },
    getFilterOption: (state) => (key: string, filterKey: string|null = null) => {
      const output = state.filterSettings.filterValues.find((f) => f.code === key);
      if (output) {
        return output;
      }
      const filter = state.filterSettings.filters.find((f) => f.code === filterKey);
      console.warn('couldnt find filter option in translation. Key: '+key);
      return  {
        name: filter ? (key.split('.')?.slice(2)?.join('.') ?? key) + ' ' + filter?.uom : key,
        code: key,
        parentCode: null,
        description: null,
        image: null,
      };
    },
    getFilterName: (state) => (key: string): string => {
      const output = state.filterSettings.filters.find((f) => f.code === key);
      if (output) {
        return output.name;
      }
      return  key;
    },
    getNavByUid: (state) => (
      _uid:string,
      findIn: 'main' | 'secondary' | 'both' | StoryBlokNestableNav[] = 'both'): StoryBlokNestableNav | null => {

      function findNode(_uid: string, array: StoryBlokNestableNav[]) : null | StoryBlokNestableNav  {
        for (const node of array) {
          if (node._uid === _uid) { return node; }
          if (node.children) {
            const child = findNode(_uid, node.children);
            if (child) { return child; }
          }
        }
        return null;
      }
      if (findIn === 'main' || findIn === 'both') {
        if (state.header?.mainNavigation) {
          const result = findNode(_uid, state.header.mainNavigation);
          if (result) { return result; }
        }
      }
      if (findIn === 'secondary' || findIn === 'both') {
        if (state.header?.secondaryNavigation) {
          const result = findNode(_uid, state.header.secondaryNavigation);
          if (result) { return result; }
        }
      }
      if (Array.isArray(findIn)) {
        return findNode(_uid, findIn);
      }
      return null;

    },
    getNavChildrenByUid: (state) => (parentUid: string): StoryBlokNestableNav[] => {
      const mainChildren = state.header?.mainNavigation.find((f)=>f._uid === parentUid)?.children;
      if (mainChildren) {
        return mainChildren;
      }
      const secondaryChildren = state.header?.secondaryNavigation.find((f)=>f._uid === parentUid)?.children;
      if (secondaryChildren) {
        return secondaryChildren;
      }
      return [];
    },
    getNavigation: (state) => (nav: 'mainNavigation' | 'secondaryNavigation'): StoryBlokNestableNav[] => {
      if (!state.header?.[nav]) {
        return [];
      }
      const { checkPermission } = useMenuHelper();

      const filterNav = (navs: StoryBlokNestableNav[]): StoryBlokNestableNav[] => {
        return navs
          .filter((nav) => {
            return checkPermission(nav.restrictedPage);
          })
          .map((nav) => ({
            ...nav,
            children: filterNav(nav.children),
          }));
      };

      return filterNav(state.header[nav]);
    },
    getFooterNav: (state) => {
      if (!state.footer?.navigation) {
        return [];
      }
      return state.footer.navigation;
    },
    getFooterMetaLinks: (state) => {
      if (!state.footer?.metaLinks) {
        return [];
      }
      return state.footer.metaLinks;
    },
    hideSiteMessage(state) {
      return state.siteNotice?.hideSiteMessage;
    },
    footerNavigation(state) {
      return state.footer?.navigation || [];
    },
    getFooter(state) {
      return state.footer as StoryBlokGlobalFooter;
    },
    articlesFolder(state) {
      return state.config?.articlesFolder || '';
    },
    brandlistPage(state) {
      return state.config?.brandlistPage || '';
    },
    getStory: (state) => (id: number) => {
      return state.stories?.find((i) => i.id === id);
    },
    getCurrentUrl(state) {
      const { fullPath } = useRoute();
      return `https://${state.hostname}${fullPath}`;
    },
    getCountryCode(state) {
      return state.domainMap?.countryCode || '';
    },
    getCountryName(state) {
      return getCountryNativeName(state.domainMap?.countryCode);
    },
    getDetectedCountryCode(state) {
      return state.detectedCountryCode;
    },
    getSuggestedMarket(state): DomainMapper | null {
      if (state.domainMap?.features?.nonEcomSite) {
        return null;
      }
      const detected = state.detectedCountryCode;
      if (!detected || detected === state.domainMap?.countryCode) {
        return null;
      }
      return findMarketByCountryCode(detected, state.domainMap?.environment ?? 'local');
    },
    getCountryUrl(state) {
      return state.domainMap?.url || '';
    },
    getEnvironment(state) {
      return state.domainMap?.environment || 'local';
    },
    getFlagUrl(state) {
      return state.domainMap?.flagUrl || '';
    },
    getLocale(state) {
      return state.domainMap?.locale || '';
    },
    getCurrency(state) {
      return state.domainMap?.currency || '';
    },
    getMeasurement(state): 'metric' | 'imperial' {
      return state.domainMap?.measurement || 'metric';
    },
    getLogoUrl(state) {
      return state.domainMap?.logoUrl || '';
    },
    getStoryblokBasePath(state) {
      return state.domainMap?.storyblokBasePath || '';
    },
    getNorceCdnBaseUrl(state): string {
      return state.domainMap?.norceCdnBaseUrl ?? 'https://kahrs.cdn-norce.tech';
    },
    getApiApplicationBasePath(state) {
      const basePath = state.domainMap?.storyblokBasePath ?? '';
      const b2bPath = state.domainMap?.b2b?.storyblokBasePath ?? '';
      const b2cPath = state.domainMap?.b2c?.storyblokBasePath ?? '';

      if (isB2b(state.customerType) && b2bPath.length) {
        return `${basePath}/${b2bPath}`;
      }

      if (isB2c(state.customerType) && b2cPath.length) {
        return `${basePath}/${b2cPath}`;
      }

      return basePath;
    },

    // Get product basepaths for both b2b and b2x
    getProductBasePaths(state) {
      return [state.domainMap?.b2c?.productBasePath || '' , state.domainMap?.b2b?.productBasePath || ''];
    },

    getProductBasePath(state) {
      if (isB2b(state.customerType)) {
        return state.domainMap?.b2b?.productBasePath || '';
      }

      if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.productBasePath || '';
      }

      return state.domainMap?.b2c?.productBasePath || '';
    },
    getIsNonEcomSite(state): boolean {
      return state.domainMap?.features?.nonEcomSite ?? false;
    },
    getNewB2CDesign(state): boolean {
      return isB2c(state.customerType) && NEW_DESIGN_COUNTRIES.includes(state.domainMap?.countryCode ?? '');
    },
    getNewRetailerDesign(state): boolean {
      return NEW_DESIGN_COUNTRIES.includes(state.domainMap?.countryCode ?? '');
    },
    getIsGlobalMarket(state): boolean {
      return state.domainMap?.countryCode === 'global';
    },
    getAlgoliaIndex(state) {
      if (isB2b(state.customerType)) {
        return state.domainMap?.b2b?.algoliaIndex ?? state.domainMap?.algoliaIndex ?? '';
      } else if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.algoliaIndex ?? state.domainMap?.algoliaIndex ?? '';
      }
      return state.domainMap?.algoliaIndex || '';
    },
    getAlgoliaIndexAutocomplete(state) {
      if (isB2b(state.customerType)) {
        return state.domainMap?.b2b?.algoliaIndexAutocomplete ?? state.domainMap?.algoliaIndexAutocomplete ?? '';
      } else if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.algoliaIndexAutocomplete ?? state.domainMap?.algoliaIndexAutocomplete ?? '';
      }
      return state.domainMap?.algoliaIndexAutocomplete || '';
    },
    getAlgoliaIndexPages(state) {
      return state.domainMap?.algoliaIndexPages || '';
    },
    getApplicationId(state) {
      if (isB2b(state.customerType)) {
        return state.domainMap?.b2b?.applicationId ?? state.domainMap?.applicationId ?? '';
      } else if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.applicationId ?? state.domainMap?.applicationId ?? '';
      }
      return state.domainMap?.applicationId || '';
    },
    getBrandName: (state) => (code: string) => {
      const name = state.brandList.find((f: BrandItem) => f.brandCode === code);
      return name?.brandName || '';
    },
    getBrandLink: (state) => (code: string): string | null => {
      const name = state.brandList.find((f: BrandItem) => f.brandCode === code);
      if (!name?.brandLink) {
        return null;
      }
      return name.brandLink.startsWith('/') ? name.brandLink : '/' + name.brandLink;
    },
    getBrandFlag: (state) => (code: string): string => {
      const name = state.brandList.find((f: BrandItem) => f.brandCode === code);
      return name?.brandFlag || '';
    },
    getSearchSuggestions(state) {
      return state.header?.popularTerms.split(',').map((m) => m.trim());
    },
    getFreeShippingThreshold(state) {
      return state.config?.freeShippingThreshold ?? 300;
    },
    getSignupFormId(state) {
      return state.config?.signupFormId || '';
    },
    getPointCollectingThreshold(state) {
      return state.pointCollectingThreshold;
    },
    getBrandPageLink(state) {
      if (state.config?.brandlistPage) {
        const { $sbLinkFix } = useNuxtApp();
        return $sbLinkFix(state.config?.brandlistPage);
      }
      return '';
    },
    getAllProductsLink(state) {
      if (state.config?.allProductsPage) {
        const { $sbLinkFix } = useNuxtApp();
        return $sbLinkFix(state.config?.allProductsPage);
      }
      return '';
    },
    getAltDeliveryTextCategories(state) {
      return state.config?.altDeliveryTextCategories?.split(',') || [];
    },
    getCurrentStoryName(state) {
      return state.currentStory?.name || '';
    },
    getCurrentStoryId(state) {
      return state.currentStory?.id || -1;
    },
    getCurrentPageType(state) {
      return state.currentStory?.content.component || '';
    },
    getAllowBackorders(state) {
      if (isB2b(state.customerType)) {
        return state.domainMap?.b2b?.features?.backorders ?? state.domainMap?.features?.backorders ?? false;
      }

      if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.features?.backorders ?? state.domainMap?.features?.backorders ?? false;
      }

      return state.domainMap?.features?.backorders ?? false;
    },
    getFastOrderEnabled(state) {
      if (isB2b(state.customerType)) {
        return state.domainMap?.b2b?.features?.fastOrder ?? state.domainMap?.features?.fastOrder ?? false;
      } else if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.features?.fastOrder ?? state.domainMap?.features?.fastOrder ?? false;
      }
      return state.domainMap?.features?.fastOrder ?? false;
    },
    getSampleInventoryEnabled(state) {
      if (isB2b(state.customerType)) {
        return state.domainMap?.b2b?.features?.sampleInventory ?? state.domainMap?.features?.sampleInventory ?? false;
      } else if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.features?.sampleInventory ?? state.domainMap?.features?.sampleInventory ?? false;
      }
      return state.domainMap?.features?.sampleInventory ?? false;
    },
    // Hidden release: rolled out market by market in production via the
    // sampleRequestNav flag, always on in local/stage/preview.
    getSampleRequestNavEnabled(state) {
      if (state.domainMap?.environment === 'local' || state.domainMap?.environment === 'stage' || state.domainMap?.environment === 'preview') {
        return true;
      }
      if (isB2b(state.customerType)) {
        return state.domainMap?.b2b?.features?.sampleRequestNav ?? state.domainMap?.features?.sampleRequestNav ?? false;
      } else if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.features?.sampleRequestNav ?? state.domainMap?.features?.sampleRequestNav ?? false;
      }
      return state.domainMap?.features?.sampleRequestNav ?? false;
    },
    // B2B / B2C settings
    getB2cStartpagePath(state) {
      return `/${state.domainMap?.storyblokBasePath}/${state.domainMap?.b2c?.storyblokBasePath}`.replace(/\/?$/, '/');
    },
    getB2bStartpagePath(state) {
      return `/${state.domainMap?.storyblokBasePath}/${state.domainMap?.b2b?.storyblokBasePath}`.replace(/\/?$/, '/');
    },
    getB2cEnabled(state) {
      /**
       * Temporary force true on german preview
       */
      return (state.domainMap?.b2c?.enabled || (state.domainMap?.environment === 'preview'))  ?? false;
    },
    getB2bEnabled(state) {
      return state.domainMap?.b2b?.enabled ?? false;
    },
    getDisplayIncVat(state) {
      if (isB2b(state.customerType)) {
        return state.domainMap?.b2b?.displayIncVat ?? state.domainMap?.displayIncVat ?? false;
      } else if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.displayIncVat ?? state.domainMap?.displayIncVat ?? false;
      }
      return state.domainMap?.displayIncVat ?? false;
    },
    getHasDifferentEndConsumerTypes(state): boolean {
      return isB2cEnabled(state.domainMap) && isB2bEnabled(state.domainMap);
    },
    getEndConsumerTypeSet(state) {
      return isCustomerTypeSet(state.customerType);
    },
    getIsB2b(state): boolean {
      return isB2b(state.customerType);
    },
    getIsSeB2b(state): boolean {
      return state.domainMap?.countryCode === 'se' && isB2b(state.customerType);
    },
    getIsB2c(state): boolean {
      return isB2c(state.customerType);
    },
    /**
     * Returns true if this is a B2B non-logged-in user with only samples in cart
     * Used to show the B2C checkout flow for sample-only orders
     */
    getIsB2bSampleCheckout(state): boolean {
      const userStore = useUserStore();
      const cartStore = useCartStore();
      return isB2b(state.customerType) &&
             !userStore.isLoggedIn &&
             cartStore.hasOnlySamples;
    },
    getConsumerTypeSettingsPath(state): string {
      return getConsumerTypeSettingsPathFromDomain(state.domainMap, state.customerType);
    },
    getPurchasesEnabled(state): boolean {
      if (isB2b(state.customerType)) {
        const userStore = useUserStore();
        const cartStore = useCartStore();
        // Allow purchase for non-logged-in B2B users with only samples
        if (!userStore.isLoggedIn && cartStore.hasOnlySamples) {
          return true;
        }
        return (state.domainMap?.b2b?.purchaseEnabled ?? true) && userStore.isLoggedIn;
      } else if (isB2c(state.customerType)) {
        return state.domainMap?.b2c?.purchaseEnabled ?? true;
      }
      return true;
    },
    /**
     * Returns true when "log in to see price" should be shown on product pages.
     * Unlike getPurchasesEnabled, this ignores cart contents (hasOnlySamples).
     */
    getShowLoginForPrice(state): boolean {
      if (isB2b(state.customerType)) {
        const userStore = useUserStore();
        return !userStore.isLoggedIn;
      }
      return false;
    },
    getContactUsPage(state): StoryBlokLink | null {
      if (state.config?.contactUsPage && state.config.contactUsPage.story) {
        return state.config.contactUsPage;
      }
      return null;
    },
    getMarketsList(state): DomainMapper[] | null {
      const environment = state.domainMap?.environment ?? 'local';
      return getMarketsByEnvironment(environment);
    },

    getFloorPlannerLocale(state) {
      if (state.domainMap?.countryCode === 'de') {
        return 'en'; // Temp fix! It should be de-DE
      }

      if (state.domainMap?.countryCode === 'us' || state.domainMap?.countryCode === 'se' || state.domainMap?.countryCode === 'gb' || state.domainMap?.countryCode === 'global' || state.domainMap?.countryCode === 'no' || state.domainMap?.countryCode === 'fi' || state.domainMap?.countryCode === 'dk' || state.domainMap?.countryCode === 'at') {
        return 'en';
      }
      return state.domainMap?.locale;
    },
    getEnableShippingToConsumer(state) {
      return !(state.domainMap?.features?.disableShipToConsumer ?? false);
    },
    getEnableCustomerEditAddress(state) {
      if (isB2c(state.customerType)) {
        return true;
      }
      const userStore = useUserStore();
      if (userStore.isSalesRepOrCustomerSuccessUser) {
        return true;
      }
      return !(state.domainMap?.features?.onlyAllowCustomerEditAddressesForSamples ?? false);
    },
    getEnableFreeShippingProgress(state) {
      return state.domainMap?.countryCode !== 'de' && state.domainMap?.countryCode !== 'se' && state.domainMap?.countryCode !== 'us' && state.domainMap?.countryCode !== 'fi' && state.domainMap?.countryCode !== 'no' && state.domainMap?.countryCode !== 'dk' && state.domainMap?.countryCode !== 'fr';
    },
    getEnableSeparateAccessoryShippingWarning(state) {
      return state.domainMap?.countryCode === 'us' && isB2c(state.customerType);
    },
    getEnableTrackAndTrace(state) {
      return state.domainMap?.countryCode !== 'us';
    },
    getEnableStateField(state) {
      return state.domainMap?.countryCode === 'us';
    },
    disableResilientPricingAndPurchase(state) {
      return state.domainMap?.countryCode === 'us' || state.domainMap?.countryCode === 'de' || state.domainMap?.countryCode === 'se' || state.domainMap?.countryCode === 'fi' || state.domainMap?.countryCode === 'no';
    },
    getEnableResilientPricing(state) {
      return state.domainMap?.countryCode === 'fi';
    },
    disablePricingAndPurchase(state) {
      return state.domainMap?.countryCode === 'us' && isB2c(state.customerType);
    },
    disablePricingB2C(state) {
      return (state.domainMap?.countryCode === 'no' || state.domainMap?.countryCode === 'us' || state.domainMap?.countryCode === 'fi' || state.domainMap?.countryCode === 'global') && isB2c(state.customerType);
    },
    disablePricingOnB2CPage(state) {
      return state.domainMap?.countryCode === 'no' || state.domainMap?.countryCode === 'us' || state.domainMap?.countryCode === 'fi' || state.domainMap?.countryCode === 'global';
    },
    getShowPriceNonLoggedInB2B(state) {
      return state.domainMap?.countryCode === 'de' && isB2b(state.customerType);
    },
    disableResilientStockStatus(state) {
      const userStore = useUserStore();
      const isUS = state.domainMap?.countryCode === 'us';

      // Only show stock status for logged-in US users
      return !(isUS && userStore.isLoggedIn);

    },
    getHideResilientProductsSearch(state) {
      return state.domainMap?.countryCode === 'fi';
    },
    disableRetailerExhibitionDetails(state) {
      return state.domainMap?.countryCode === 'us' || state.domainMap?.countryCode === 'de' || state.domainMap?.countryCode === 'fi' || state.domainMap?.countryCode === 'at' || NEW_DESIGN_COUNTRIES.includes(state.domainMap?.countryCode ?? '');
    },
    disableBookRetailerMeeting(state) {
      return state.domainMap?.countryCode === 'us' || state.domainMap?.countryCode === 'de' || state.domainMap?.countryCode === 'dk' || state.domainMap?.countryCode === 'at';
    },
    showRetailerPostalCode(state) {
      return state.domainMap?.countryCode === 'de';
    },
    getEnableOrderProposalRequest(state) {
      if (!state.enableOrderProposalRequest) {
        return false;
      }

      if (state.restrictOrderProposalToCsOnly) {
        const userStore = useUserStore();
        return userStore.isCustomerSuccessUser;
      }

      return true;
    },
    getHasDeliveryExtras(state) {
      return isB2b(state.customerType) && Boolean(state.tailLiftPartNo || state.timedUnloadingPartNo);
    },
    getEnableBreakModeOfDelivery(state) {
      return Boolean(state.outsideModDeliveryFeePartNo?.length);
    },
    getEnableCheckoutV2(state): boolean {
      const domainMap = state.domainMap;

      if (!domainMap) {
        return false;
      }

      if (isB2b(state.customerType)) {
        return Boolean(domainMap.b2b?.features?.enableCheckoutV2);
      }

      if (isB2c(state.customerType)) {
        return Boolean(domainMap.b2c?.features?.enableCheckoutV2);
      }

      return Boolean(domainMap.features?.enableCheckoutV2);
    },
    /**
     * Enable Norce Checkout address updates for B2C
     * When enabled, B2C checkout will use Norce Checkout API for address updates
     * instead of the standard Norce Commerce API
     */
    getEnableNorceCheckoutAddressUpdates(state): boolean {
      const domainMap = state.domainMap;

      if (!domainMap) {
        return false;
      }

      // Only available for B2C
      if (!isB2c(state.customerType)) {
        return false;
      }

      return Boolean(domainMap.b2c?.features?.enableNorceCheckoutAddressUpdates);
    },
    getEnableDisplayPreferredShippingDisclaimer(state) {
      return state.domainMap?.countryCode !== 'se';
    },
    getNorceCheckoutEnabled(state): boolean {
      return state.norceCheckoutEnabled;
    },
    enableOrderProposalForCS(state) {
      const userStore = useUserStore();
      return userStore.isCustomerSuccessUser && (state.domainMap?.countryCode === 'us' || state.domainMap?.countryCode === 'gb' || state.domainMap?.countryCode === 'de');
    },
    getMaxDiscount(state): number {
      const userStore = useUserStore();
      if (userStore.isSalesRepUser) {
        return state.maxDiscountSalesrep;
      }
      return state.maxDiscount;
    },
    getSplitPONumberRequisition(state): boolean {
      return state.domainMap?.countryCode === 'fi';
    },
    hideSRP(state) {
      // Hide Standard price for global market or if company has flag from Norce
      if (state.domainMap?.countryCode === 'global') {
        return true;
      }
      const userStore = useUserStore();
      return userStore.userProfile.company?.flags?.some((flag: { name: string }) => flag.name === 'HideSRP');
    },
    hasSurchargeFlag() {
      // Check if company has Surcharge flag from Norce (case-insensitive)
      const userStore = useUserStore();
      return userStore.userProfile.company?.flags?.some(
        (flag: { name: string }) => flag.name.toLowerCase() === 'surcharge'
      );
    },
    /**
     * Determines if surcharge notice should be shown based on config settings:
     * - User must be logged in and B2B
     * - If showSurchargeNoticeForAllCompanies is true: show for all B2B users
     * - If showSurchargeNoticeForFlaggedCompanies is true: show only for companies with surcharge flag
     * - If neither is checked: hide notice
     */
    showSurchargeNotice(state): boolean {
      const userStore = useUserStore();

      if (!userStore.isLoggedIn || !isB2b(state.customerType)) {
        return false;
      }

      if (state.showSurchargeNoticeForAllCompanies) {
        return true;
      }

      if (state.showSurchargeNoticeForFlaggedCompanies) {
        return this.hasSurchargeFlag ?? false;
      }

      return false;
    },
    getDisableExcludeFromCustomerBonus(state) {
      return state.domainMap?.countryCode === 'fi' || state.domainMap?.countryCode === 'fr';
    },
    getShowEcoTax(state) {
      return state.domainMap?.countryCode === 'fr';
    },
    getFreightBeforeSubtotal(state) {
      return state.domainMap?.countryCode === 'fr';
    },
    getDisableInvoices(state) {
      return state.domainMap?.countryCode === 'fi';
    },
    getHideEmailAddressFromInvoiceAddress(state) {
      return state.domainMap?.countryCode === 'fi';
    },
    getPreCheckOptionsCheckout(state): boolean {
      return state.domainMap?.countryCode === 'no';
    },
    getHideSampleCartText(state): boolean {
      return state.domainMap?.countryCode === 'us';
    },
    getFreeConsumerShippingIfSamples(state): boolean {
      return state.domainMap?.countryCode === 'se' || state.domainMap?.countryCode === 'gb' || state.domainMap?.countryCode === 'dk';
    },
    getHideNewsletterCheckout(state) {
      return state.domainMap?.countryCode === 'fi';
    },
    /**
     * TEMP disable auto tds in prod
     * When false, only show TDS links from Norce parametric data (ItemTDSlink).
     */
    getEnableAutoTds(state): boolean {
      return state.domainMap?.environment !== 'production';
    },
    hidePricesForSamplesOnly() {
      const userStore = useUserStore();
      return userStore.isSamplesOnlyUser;
    },
  },
});
